/**
 * What a failed recording upload means for the recorder (UC-8, light). The capture id is the
 * idempotency key, so the same recording may always go again: after no answer, a timeout, a
 * server or gateway error, the audit trail or the extension being unavailable, or bytes that
 * arrived damaged (`maySendAgain`). A refusal of the recording itself is final: sending the same
 * bytes again gets the same answer. A code this does not know reads as a general refusal, never
 * as a success.
 */
import type {
  RecordingSaveProblem,
  RecordingSaveStatus,
} from '@/components/humint/questioning/recording-types'
import { classifySaveError, type SaveRefusals } from '../mappers/errors'

export type RecordingSendFailure =
  | { kind: 'cancelled' }
  | Extract<RecordingSaveStatus, { kind: 'failed' }>

/** The refusals a recording has its own words for, beside what every save says. */
const REFUSALS: SaveRefusals<RecordingSaveProblem> = {
  byStatus: { 413: 'too_large' },
  byCode: {
    capture_too_large: 'too_large',
    unsupported_format: 'unsupported_format',
    unsupported_language: 'language',
    language_not_allowed: 'language',
    subject_not_in_case: 'subject_gone',
    // The bytes arrived damaged: the same recording goes again.
    sha256_mismatch: 'error',
  },
}

/**
 * Classify a failed recording upload. `online` is `navigator.onLine`: no answer while offline
 * reads "no connection", otherwise a general failure.
 */
export function classifyRecordingSendError(error: unknown, online: boolean): RecordingSendFailure {
  const failure = classifySaveError(error, online, REFUSALS)
  return failure.kind === 'cancelled' ? failure : { kind: 'failed', problem: failure.problem }
}
